import React, { useEffect, useState, useCallback } from "react";
import { authApi } from "../../services/authApi";
import { documentApi } from "../../services/documentApi";
import { StatusBadge } from "../../components/StatusBadge";
import { EagleState } from "../../components/EagleState";
import { PageHelpBanner } from "../../components/PageHelpBanner";
import { useLocalization } from "../../hooks/useLocalization";
import { formatDate } from "../../lib/date";
import { REASON_CATEGORIES } from "../../lib/constants";
import { AccessDenied } from "../../components/AccessDenied";

export default function DocumentReviewQueuePage() {
  const { t } = useLocalization();
  const [documents, setDocuments] = useState([]);
  const [reviewers, setReviewers] = useState([]);
  const [filters, setFilters] = useState({ overdue_only: false, unassigned_only: false, sort_by: "oldest" });
  const [selected, setSelected] = useState(null);
  const [decision, setDecision] = useState("");
  const [reasonCategory, setReasonCategory] = useState("");
  const [reviewNotes, setReviewNotes] = useState("");
  const [employeeInstructions, setEmployeeInstructions] = useState("");
  const [error, setError] = useState("");
  const [accessDenied, setAccessDenied] = useState(false);

  const load = useCallback(() => {
    documentApi.queue(filters)
      .then((r) => { setDocuments(r.data); setAccessDenied(false); })
      .catch((err) => { if (err.response?.status === 403) setAccessDenied(true); });
  }, [filters]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { authApi.listUsers().then((r) => setReviewers(r.data)).catch(() => {}); }, []);

  const openReview = (doc) => {
    setSelected(doc);
    setDecision("");
    setReasonCategory("");
    setReviewNotes("");
    setEmployeeInstructions("");
    setError("");
  };

  const submitReview = async () => {
    setError("");
    if (decision !== "APPROVED" && (!reasonCategory || !reviewNotes.trim())) {
      setError(t("documentReview.rejectRequiresReason"));
      return;
    }
    try {
      await documentApi.review(selected.id, {
        decision,
        reason_category: decision === "APPROVED" ? null : reasonCategory,
        review_notes: decision === "APPROVED" ? null : reviewNotes,
        employee_instructions: decision === "APPROVED" ? null : employeeInstructions,
      });
      setSelected(null);
      load();
    } catch (err) {
      setError(err.response?.data?.detail || t("common.somethingWentWrong"));
    }
  };

  return (
    <div data-testid="document-review-queue-page">
      {accessDenied ? (
        <AccessDenied resourceLabel={t("documentReview.title")} />
      ) : (
      <>
      <PageHelpBanner pageKey="document-review-queue" title="How to use this page">
        <p>Every document an employee uploads during onboarding lands here for review. Click <strong>Review</strong> to open a document, then Approve, Reject (reason required), or Request Correction with instructions for the employee. Use the checkboxes above to filter to overdue or unassigned items first.</p>
      </PageHelpBanner>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <h2 className="text-2xl font-bold tracking-tight text-gray-900">{t("documentReview.title")}</h2>
        <div className="flex items-center gap-3 text-sm">
          <label className="flex items-center gap-1.5">
            <input data-testid="filter-overdue-only" type="checkbox" checked={filters.overdue_only} onChange={(e) => setFilters((f) => ({ ...f, overdue_only: e.target.checked }))} />
            {t("documentReview.overdueOnly")}
          </label>
          <label className="flex items-center gap-1.5">
            <input data-testid="filter-unassigned-only" type="checkbox" checked={filters.unassigned_only} onChange={(e) => setFilters((f) => ({ ...f, unassigned_only: e.target.checked }))} />
            {t("documentReview.unassignedOnly")}
          </label>
          <select data-testid="sort-select" value={filters.sort_by} onChange={(e) => setFilters((f) => ({ ...f, sort_by: e.target.value }))} className="px-3 py-1.5 rounded-full border border-gray-300 text-xs">
            <option value="oldest">Oldest first</option>
            <option value="newest">Newest first</option>
            <option value="employee_name">Employee name</option>
            <option value="store">Store</option>
            <option value="document_type">Document type</option>
          </select>
        </div>
      </div>

      {documents.length === 0 ? (
        <EagleState variant="empty" title={t("eagle.noPendingReviewsTitle")} message={t("eagle.noPendingReviewsMessage")} testId="document-review-empty" />
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
          <table className="w-full text-sm min-w-[640px]">
            <thead className="bg-gray-50 text-gray-500 text-left">
              <tr>
                <th className="px-4 py-3 font-medium">{t("documentReview.employeeName")}</th>
                <th className="px-4 py-3 font-medium">{t("documentReview.store")}</th>
                <th className="px-4 py-3 font-medium">{t("documentReview.documentType")}</th>
                <th className="px-4 py-3 font-medium">{t("documentReview.fileName")}</th>
                <th className="px-4 py-3 font-medium">{t("documentReview.uploadDate")}</th>
                <th className="px-4 py-3 font-medium">{t("documentReview.daysWaiting")}</th>
                <th className="px-4 py-3 font-medium">{t("common.status")}</th>
                <th className="px-4 py-3 font-medium">{t("common.actions")}</th>
              </tr>
            </thead>
            <tbody>
              {documents.map((doc) => (
                <tr key={doc.id} className="border-t border-gray-100 hover:bg-gray-50" data-testid={`document-queue-row-${doc.id}`}>
                  <td className="px-4 py-3">{doc.employee_name} <span className="text-gray-500">({doc.employee_code})</span></td>
                  <td className="px-4 py-3">{doc.store_location}</td>
                  <td className="px-4 py-3">{doc.category}</td>
                  <td className="px-4 py-3 max-w-[160px] truncate">{doc.original_filename}</td>
                  <td className="px-4 py-3">{formatDate(doc.created_at)}</td>
                  <td className="px-4 py-3">{doc.days_waiting}</td>
                  <td className="px-4 py-3"><StatusBadge status={doc.status} /></td>
                  <td className="px-4 py-3">
                    <button data-testid={`review-document-${doc.id}`} onClick={() => openReview(doc)} className="text-blue-600 font-semibold hover:underline">Review</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {selected && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="document-review-drawer">
          <div className="bg-white rounded-2xl w-full max-w-lg p-6 shadow-xl space-y-4">
            <h3 className="text-lg font-bold text-gray-900">{selected.employee_name} — {selected.category}</h3>
            <p className="text-xs text-gray-500">Checksum: {selected.checksum_sha256?.slice(0, 16)}...</p>
            <div className="flex gap-2">
              <button data-testid="approve-document-button" onClick={() => setDecision("APPROVED")} className={`px-4 py-2 rounded-full text-xs font-semibold ${decision === "APPROVED" ? "bg-emerald-500 text-white" : "border border-emerald-300 text-emerald-600"}`}>{t("common.approve")}</button>
              <button data-testid="reject-document-button" onClick={() => setDecision("REJECTED")} className={`px-4 py-2 rounded-full text-xs font-semibold ${decision === "REJECTED" ? "bg-red-500 text-white" : "border border-red-300 text-red-500"}`}>{t("common.reject")}</button>
              <button data-testid="request-correction-button" onClick={() => setDecision("NEEDS_CORRECTION")} className={`px-4 py-2 rounded-full text-xs font-semibold ${decision === "NEEDS_CORRECTION" ? "bg-amber-500 text-white" : "border border-amber-300 text-amber-600"}`}>Request Correction</button>
            </div>
            {decision && decision !== "APPROVED" && (
              <div className="space-y-3">
                <select data-testid="reason-category-select" value={reasonCategory} onChange={(e) => setReasonCategory(e.target.value)} className="w-full px-4 py-2.5 rounded-lg border border-gray-300 text-sm">
                  <option value="">{t("documentReview.reasonCategory")}</option>
                  {REASON_CATEGORIES.map((r) => <option key={r} value={r}>{t(`documentReview.reason_${r}`)}</option>)}
                </select>
                <textarea data-testid="review-notes-input" value={reviewNotes} onChange={(e) => setReviewNotes(e.target.value)} placeholder={t("documentReview.explanation")} className="w-full px-4 py-2.5 rounded-lg border border-gray-300 text-sm" rows={2} />
                <textarea data-testid="employee-instructions-input" value={employeeInstructions} onChange={(e) => setEmployeeInstructions(e.target.value)} placeholder={t("documentReview.employeeInstructions")} className="w-full px-4 py-2.5 rounded-lg border border-gray-300 text-sm" rows={2} />
              </div>
            )}
            {error && <p className="text-sm text-red-500" data-testid="document-review-error">{error}</p>}
            <div className="flex gap-2">
              <button data-testid="cancel-document-review-button" onClick={() => setSelected(null)} className="flex-1 py-2 rounded-full border border-gray-300 text-sm font-semibold">{t("common.cancel")}</button>
              <button data-testid="submit-document-review-button" onClick={submitReview} disabled={!decision} className="flex-1 py-2 rounded-full bg-blue-600 text-white text-sm font-semibold disabled:opacity-40">{t("common.submit")}</button>
            </div>
          </div>
        </div>
      )}
      </>
      )}
    </div>
  );
}
